import { UniqueIdentifier } from '@dnd-kit/core';
import { GlobalVariable } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import globalVariablesManager from '@kemu-io/kemu-core/common/managers/globalVariables.js';

export type FlattenedItem = {
  id: string;
  variable: GlobalVariable;
  children: string[];
  collapsed: boolean;
  depth: number;
  parentId: string | null;
};

export type SensorContext = {
  items: FlattenedItem[];
  offset: number;
};

/**
 * Calculates the projection (depth and parentId) for a dragged item
 * based on its horizontal offset and the target item
 */
export const getProjection = (
  items: FlattenedItem[],
  activeId: UniqueIdentifier,
  overId: UniqueIdentifier,
  dragOffset: number,
  indentationWidth: number
): { depth: number; parentId: string | null } | null => {
  const overItemIndex = items.findIndex(({ id }) => id === overId);
  const activeItemIndex = items.findIndex(({ id }) => id === activeId);

  if (overItemIndex === -1 || activeItemIndex === -1) {
    return null;
  }

  const activeItem = items[activeItemIndex];
  const overItem = items[overItemIndex];

  // Calculate the depth based on horizontal offset
  const dragDepth = Math.round(dragOffset / indentationWidth);
  const projectedDepth = overItem.depth + dragDepth;
  const maxDepth = getMaxDepth(activeItem);
  const minDepth = getMinDepth(activeItem);

  let depth = projectedDepth;
  if (projectedDepth >= maxDepth) {
    depth = maxDepth;
  } else if (projectedDepth < minDepth) {
    depth = minDepth;
  }

  // Find the parent based on the calculated depth
  const parentId = getParentId(items, overItemIndex, depth);

  return { depth, parentId };
};

/**
 * Gets the maximum depth an item can be nested
 */
const getMaxDepth = (item: FlattenedItem): number => {
  if (item.children.length === 0) {
    return item.depth;
  }
  return item.depth + 1;
};

/**
 * Gets the minimum depth an item can be (0 for root level)
 */
const getMinDepth = (item: FlattenedItem): number => {
  return 0;
};

/**
 * Finds the parent ID for a given depth at a specific index
 */
const getParentId = (
  items: FlattenedItem[],
  overIndex: number,
  depth: number
): string | null => {
  if (depth === 0 || !items[overIndex]) {
    return null;
  }

  // Look backwards from the overIndex to find the parent
  for (let i = overIndex; i >= 0; i--) {
    const item = items[i];
    if (item.depth === depth - 1) {
      // Only allow nesting into groups (both groups and regular variables can only nest into groups)
      const isItemGroup = globalVariablesManager.isGroupVariable(item.variable);
      if (isItemGroup) {
        return item.id;
      }
      // If the item at this depth is not a group, return null to prevent nesting
      return null;
    }
  }

  return null;
};

/**
 * Removes children of specified items from the flattened list
 */
export const removeChildrenOf = (
  items: FlattenedItem[],
  ids: UniqueIdentifier[]
): FlattenedItem[] => {
  const excludeParentIds = [...ids];

  return items.filter((item) => {
    if (item.parentId && excludeParentIds.includes(item.parentId)) {
      if (item.children.length) {
        excludeParentIds.push(item.id);
      }
      return false;
    }

    return true;
  });
};

/**
 * Counts the total number of children (including nested) for an item
 */
export const getChildCount = (
  items: FlattenedItem[],
  id: UniqueIdentifier
): number => {
  const item = items.find((item) => item.id === id);
  if (!item) {
    return 0;
  }

  let count = item.children.length;
  for (const childId of item.children) {
    count += getChildCount(items, childId);
  }

  return count;
};

/**
 * Flattens a tree structure into a list with depth and parent information
 */
export const flattenTree = (
  tree: Array<{
    variable: GlobalVariable;
    children: Array<{
      variable: GlobalVariable;
      children: any[];
      depth: number;
    }>;
    depth: number;
  }>,
  expandedItems: Set<string> = new Set()
): FlattenedItem[] => {
  const flattened: FlattenedItem[] = [];

  const traverse = (
    nodes: Array<{
      variable: GlobalVariable;
      children: any[];
      depth: number;
    }>,
    parentId: string | null = null
  ) => {
    for (const node of nodes) {
      const isGroup = globalVariablesManager.isGroupVariable(node.variable);
      const childrenIds = node.children.map((child) => child.variable.name);
      const isExpanded = expandedItems.has(node.variable.name);

      flattened.push({
        id: node.variable.name,
        variable: node.variable,
        children: childrenIds,
        collapsed: !isExpanded,
        depth: node.depth,
        parentId,
      });

      // Only traverse children if expanded
      if (isExpanded && node.children.length > 0) {
        traverse(node.children, node.variable.name);
      }
    }
  };

  traverse(tree);
  return flattened;
};

